import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Menu, X, Globe, ShoppingBag, ShoppingCart } from 'lucide-react';

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_cf4caf13-b586-4cda-9c41-d59090cfa577/artifacts/jj63lgza_Logo%20Firma_White%20logo.png";

export const Navigation = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { totalQuantity } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t('Home', 'Inicio') },
    { path: '/music', label: t('Music', 'Música') },
    { path: '/soundtracks', label: t('Soundtracks', 'Pistas') },
    { path: '/videos', label: t('Videos', 'Videos') },
    { path: '/about', label: t('About', 'Acerca de') },
    { path: '/contact', label: t('Contact', 'Contacto') },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gold-200' 
          : 'bg-charcoal'
      }`}
      data-testid="main-navigation"
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src={LOGO_WHITE} 
              alt="Sarah Capeles" 
              className={`h-14 md:h-16 w-auto transition-all duration-300 ${
                isScrolled ? 'brightness-0' : ''
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-lato text-sm uppercase tracking-wider transition-colors duration-300 ${
                  location.pathname === link.path
                    ? isScrolled ? 'text-gold-600 font-semibold' : 'text-gold-400 font-semibold'
                    : isScrolled ? 'text-charcoal hover:text-gold-600' : 'text-white/80 hover:text-gold-400'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side controls */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1.5 font-lato text-sm transition-colors ${
                isScrolled ? 'text-charcoal hover:text-gold-600' : 'text-white/80 hover:text-gold-400'
              }`}
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ES' : 'EN'}
            </button>
            <Link
              to="/cart"
              className={`relative flex items-center transition-colors ${
                isScrolled ? 'text-charcoal hover:text-gold-600' : 'text-white/80 hover:text-gold-400'
              }`}
              data-testid="nav-cart-link"
              aria-label={t('Cart', 'Carrito')}
            >
              <ShoppingCart className="w-5 h-5" />
              {totalQuantity > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-gold-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  data-testid="cart-badge"
                >
                  {totalQuantity}
                </span>
              )}
            </Link>
            <Link to="/music">
              <Button 
                className={`font-lato text-xs uppercase tracking-wider px-4 py-2 rounded-none transition-all duration-300 ${
                  isScrolled 
                    ? 'bg-gold-500 hover:bg-gold-400 text-white'
                    : 'bg-gold-500 hover:bg-gold-400 text-white'
                }`}
                data-testid="nav-shop-button"
              >
                <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                {t('Store', 'Tienda')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-charcoal' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-charcoal' : 'text-white'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-charcoal border-t border-white/10">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-lato text-sm uppercase tracking-wider py-2 ${
                    location.pathname === link.path
                      ? 'text-gold-400 font-semibold'
                      : 'text-white/80 hover:text-gold-400'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-1.5 font-lato text-sm text-white/80 hover:text-gold-400"
                >
                  <Globe className="w-4 h-4" />
                  {language === 'en' ? 'ES' : 'EN'}
                </button>
                <Link
                  to="/cart"
                  className="relative flex items-center gap-1.5 font-lato text-sm text-white/80 hover:text-gold-400"
                  data-testid="mobile-nav-cart-link"
                >
                  <ShoppingCart className="w-4 h-4" />
                  {t('Cart', 'Carrito')}
                  {totalQuantity > 0 && (
                    <span className="ml-1 bg-gold-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] inline-flex items-center justify-center px-1">
                      {totalQuantity}
                    </span>
                  )}
                </Link>
                <Link to="/music">
                  <Button className="bg-gold-500 hover:bg-gold-400 text-white font-lato text-xs uppercase tracking-wider px-4 py-2 rounded-none">
                    <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                    {t('Store', 'Tienda')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
